/**
 * Normalizes a menu item from JSON (handles both string and object formats).
 * @param {string|object} item - Raw item from JSON
 * @returns {{ name: string, desc: string|null, img: string|null }}
 */
export const normalizeItem = (item) => {
  if (typeof item === 'string') {
    return { name: item, desc: null, img: null, price: null };
  }
  return {
    name: item.name || '',
    desc: item.desc || null,
    img: item.img || null,
    price: item.price ?? null,
  };
};

/**
 * Detects if a menu item name contains non-veg keywords.
 */
const NONVEG_KEYWORDS = ['Chicken', 'Egg', 'Shrimp', 'Lamb', 'Goat', 'Mutton'];

export const isNonVeg = (name) =>
  NONVEG_KEYWORDS.some((keyword) => name.includes(keyword));

/**
 * Determines if a section should render in single-column "compact" layout.
 */
const COMPACT_SECTIONS = ['Breads', 'Chaats'];

export const isCompactSection = (title) => COMPACT_SECTIONS.includes(title);

/**
 * Layout choices offered when an admin adds a menu category.
 * `id` is what buildSection() switches on.
 */
export const CATEGORY_LAYOUTS = [
  { id: 'list', label: 'Simple list', hint: 'One list of items — like Breads or Chaats.' },
  {
    id: 'diet',
    label: 'Veg / Non-Veg',
    hint: 'Two columns with green/red diet dots — like Appetizers or Curries.',
  },
  {
    id: 'matrix',
    label: 'Price matrix',
    hint: 'One row per style with a price per base — like the Dosa grid.',
    needsColumns: true,
  },
];

/**
 * Build an empty section in whichever shape the admin picked.
 * Shapes match what SectionCard already renders, so a new category behaves
 * exactly like a shipped one from its first item on.
 */
export const buildSection = ({ title, icon, layout, columns = [] }) => {
  const base = { title, ...(icon ? { icon } : {}) };
  if (layout === 'matrix') return { ...base, priceColumns: columns, rows: [] };
  if (layout === 'diet') return { ...base, dietColumns: true, columns: [[], []] };
  return { ...base, columns: [[]] };
};

/* ══════════════════════════════════════════════════════════════════════════
   Category order
   ─────────────────────────────────────────────────────────────────────────
   The menu renders the combos block and the sections as one sequence, but
   they live in two different places in the document. These helpers present
   that sequence as a single list so reordering can treat every category the
   same, and write the result back into both places.

   `combosIndex` is where the combos block sits in that combined list. It is
   absent on menus published before categories could be reordered, which reads
   as 0 — the position combos has always had.
   ══════════════════════════════════════════════════════════════════════════ */

/** Identifies the combos block in an ordered-category list. */
export const COMBOS_ID = '__combos__';

const clamp = (n, lo, hi) => Math.min(Math.max(n, lo), hi);

/**
 * The categories in render order.
 *
 * @param menu            the whole menu document
 * @param withCombos      include the combos block (the page hides it from
 *                        customers when it holds nothing, but an editor still
 *                        needs to see and move it)
 * @returns entries of `{ id, type, section?, index? }`, where `index` is the
 *          position in `menu.sections` — the handle rename/delete still need.
 */
export function orderedCategories(menu, { withCombos = true } = {}) {
  const sections = menu?.sections || [];
  const entries = sections.map((section, index) => ({
    id: section.title, type: 'section', section, index,
  }));
  if (!withCombos) return entries;
  entries.splice(clamp(menu?.combosIndex ?? 0, 0, sections.length), 0,
    { id: COMBOS_ID, type: 'combos' });
  return entries;
}

/** Move the category at position `from` to position `to` in that list. */
export function moveCategory(menu, from, to) {
  const list = orderedCategories(menu);
  if (from === to || from < 0 || to < 0 || from >= list.length || to >= list.length) return menu;

  const next = [...list];
  next.splice(to, 0, ...next.splice(from, 1));
  const combosIndex = next.findIndex((e) => e.type === 'combos');
  return {
    ...menu,
    sections: next.filter((e) => e.type === 'section').map((e) => e.section),
    combosIndex,
  };
}

/** Append a new, empty category. */
export function addSection(menu, spec) {
  return { ...menu, sections: [...(menu?.sections || []), buildSection(spec)] };
}

/** Rename the section at `index`. */
export function renameSection(menu, index, title) {
  return {
    ...menu,
    sections: menu.sections.map((s, i) => (i === index ? { ...s, title } : s)),
  };
}

/**
 * Delete the section at `index`.
 *
 * Combos is pinned by position, not by neighbour, so removing a section above
 * it has to pull it up too — otherwise it drifts one place down the menu on
 * every delete.
 */
export function deleteSection(menu, index) {
  const combosIndex = menu.combosIndex ?? 0;
  return {
    ...menu,
    sections: menu.sections.filter((_, i) => i !== index),
    combosIndex: combosIndex > index ? combosIndex - 1 : combosIndex,
  };
}
